import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Instagram, Facebook, Youtube, Twitter, Linkedin, 
  Mail, Phone, MapPin, Clock, Globe, MessageCircle,
  Save, RefreshCw, Check, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SiteSettings = ({ theme }) => {
  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('social');
  
  // Social Media State
  const [socialMedia, setSocialMedia] = useState({
    instagram: '',
    facebook: '',
    youtube: '',
    twitter: '',
    linkedin: '',
    pinterest: ''
  });
  
  // Contact Info State
  const [contactInfo, setContactInfo] = useState({
    primary_email: '',
    secondary_email: '',
    primary_phone: '',
    secondary_phone: '',
    whatsapp: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    google_maps_url: '',
    office_hours: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [socialRes, contactRes] = await Promise.all([
        axios.get(`${API}/api/settings/social-media`, { headers, withCredentials: true }),
        axios.get(`${API}/api/settings/contact-info`, { headers, withCredentials: true })
      ]);
      
      setSocialMedia(socialRes.data);
      setContactInfo(contactRes.data);
    } catch (err) {
      console.error('Error loading settings:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSocialMedia = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/api/settings/social-media`, socialMedia, { headers, withCredentials: true });
      toast.success('Social media links saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save social media links');
    } finally {
      setSaving(false);
    }
  };

  const saveContactInfo = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/api/settings/contact-info`, contactInfo, { headers, withCredentials: true });
      toast.success('Contact information saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save contact information');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Site Settings</h2>
          <p className="text-muted-foreground">Manage social media links and contact information</p>
        </div>
        <Button variant="outline" onClick={loadSettings}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="social" className="flex items-center gap-2">
            <Instagram className="w-4 h-4" /> Social Media
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <Phone className="w-4 h-4" /> Contact Info
          </TabsTrigger>
        </TabsList>

        {/* Social Media Tab */}
        <TabsContent value="social" className="space-y-6 mt-6">
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Social Media Links
              </CardTitle>
              <CardDescription>
                Add your social media profile URLs. These will appear on the website footer and contact page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Instagram */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <Label>Instagram</Label>
                  <Input
                    placeholder="https://instagram.com/your-handle"
                    value={socialMedia.instagram}
                    onChange={(e) => setSocialMedia({...socialMedia, instagram: e.target.value})}
                  />
                </div>
                {socialMedia.instagram && (
                  <Button variant="ghost" size="icon" asChild>
                    <a href={socialMedia.instagram} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>

              {/* Facebook */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center">
                  <Facebook className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <Label>Facebook</Label>
                  <Input
                    placeholder="https://facebook.com/your-page"
                    value={socialMedia.facebook}
                    onChange={(e) => setSocialMedia({...socialMedia, facebook: e.target.value})}
                  />
                </div>
                {socialMedia.facebook && (
                  <Button variant="ghost" size="icon" asChild>
                    <a href={socialMedia.facebook} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>

              {/* YouTube */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-red-600 flex items-center justify-center">
                  <Youtube className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <Label>YouTube</Label>
                  <Input
                    placeholder="https://youtube.com/@your-channel"
                    value={socialMedia.youtube}
                    onChange={(e) => setSocialMedia({...socialMedia, youtube: e.target.value})}
                  />
                </div>
                {socialMedia.youtube && (
                  <Button variant="ghost" size="icon" asChild>
                    <a href={socialMedia.youtube} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>

              {/* Twitter/X */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-black flex items-center justify-center">
                  <Twitter className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <Label>Twitter / X</Label>
                  <Input
                    placeholder="https://twitter.com/your-handle"
                    value={socialMedia.twitter}
                    onChange={(e) => setSocialMedia({...socialMedia, twitter: e.target.value})}
                  />
                </div>
                {socialMedia.twitter && (
                  <Button variant="ghost" size="icon" asChild>
                    <a href={socialMedia.twitter} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>

              {/* LinkedIn */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-700 flex items-center justify-center">
                  <Linkedin className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <Label>LinkedIn</Label>
                  <Input
                    placeholder="https://linkedin.com/company/your-company"
                    value={socialMedia.linkedin}
                    onChange={(e) => setSocialMedia({...socialMedia, linkedin: e.target.value})}
                  />
                </div>
                {socialMedia.linkedin && (
                  <Button variant="ghost" size="icon" asChild>
                    <a href={socialMedia.linkedin} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveSocialMedia} disabled={saving}>
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Social Media Links
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Info Tab */}
        <TabsContent value="contact" className="space-y-6 mt-6">
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Contact Information
              </CardTitle>
              <CardDescription>
                This information will appear on the Contact Us page, Footer, and throughout the website.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Section */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  Email Addresses
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Primary Email</Label>
                    <Input
                      type="email"
                      placeholder="info@yourdomain.com"
                      value={contactInfo.primary_email}
                      onChange={(e) => setContactInfo({...contactInfo, primary_email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Secondary Email (Optional)</Label>
                    <Input
                      type="email"
                      placeholder="support@yourdomain.com"
                      value={contactInfo.secondary_email}
                      onChange={(e) => setContactInfo({...contactInfo, secondary_email: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Phone Section */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-500" />
                  Phone Numbers
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Primary Phone</Label>
                    <Input
                      placeholder="+91 98765 43210"
                      value={contactInfo.primary_phone}
                      onChange={(e) => setContactInfo({...contactInfo, primary_phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Secondary Phone (Optional)</Label>
                    <Input
                      placeholder="+91 98765 43211"
                      value={contactInfo.secondary_phone}
                      onChange={(e) => setContactInfo({...contactInfo, secondary_phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>WhatsApp Number</Label>
                    <Input
                      placeholder="+91 98765 43210"
                      value={contactInfo.whatsapp}
                      onChange={(e) => setContactInfo({...contactInfo, whatsapp: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-red-500" />
                  Office Address
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Address Line 1</Label>
                    <Input
                      placeholder="Building name, Street"
                      value={contactInfo.address_line1}
                      onChange={(e) => setContactInfo({...contactInfo, address_line1: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Address Line 2</Label>
                    <Input
                      placeholder="Area, Landmark"
                      value={contactInfo.address_line2}
                      onChange={(e) => setContactInfo({...contactInfo, address_line2: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      placeholder="Mumbai"
                      value={contactInfo.city}
                      onChange={(e) => setContactInfo({...contactInfo, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      placeholder="Maharashtra"
                      value={contactInfo.state}
                      onChange={(e) => setContactInfo({...contactInfo, state: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Pincode</Label>
                    <Input
                      placeholder="400001"
                      value={contactInfo.pincode}
                      onChange={(e) => setContactInfo({...contactInfo, pincode: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input
                      placeholder="India"
                      value={contactInfo.country}
                      onChange={(e) => setContactInfo({...contactInfo, country: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  Additional Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Office Hours</Label>
                    <Input
                      placeholder="Mon-Sat: 9:00 AM - 6:00 PM"
                      value={contactInfo.office_hours}
                      onChange={(e) => setContactInfo({...contactInfo, office_hours: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Google Maps URL (Optional)</Label>
                    <Input
                      placeholder="https://maps.google.com/..."
                      value={contactInfo.google_maps_url}
                      onChange={(e) => setContactInfo({...contactInfo, google_maps_url: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveContactInfo} disabled={saving}>
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Contact Information
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle className="text-sm">Preview</CardTitle>
              <CardDescription>How your contact info will appear on the website</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email
                  </h4>
                  {contactInfo.primary_email && <p>{contactInfo.primary_email}</p>}
                  {contactInfo.secondary_email && <p className="text-muted-foreground">{contactInfo.secondary_email}</p>}
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Phone
                  </h4>
                  {contactInfo.primary_phone && <p>{contactInfo.primary_phone}</p>}
                  {contactInfo.secondary_phone && <p className="text-muted-foreground">{contactInfo.secondary_phone}</p>}
                  {contactInfo.whatsapp && (
                    <p className="flex items-center gap-1 text-green-600">
                      <MessageCircle className="w-3 h-3" /> {contactInfo.whatsapp}
                    </p>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Address
                  </h4>
                  {contactInfo.address_line1 && <p>{contactInfo.address_line1}</p>}
                  {contactInfo.address_line2 && <p>{contactInfo.address_line2}</p>}
                  <p>
                    {[contactInfo.city, contactInfo.state, contactInfo.pincode].filter(Boolean).join(', ')}
                  </p>
                  {contactInfo.country && <p>{contactInfo.country}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default SiteSettings;
